import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Q1Page() {
  const [showNormal, setShowNormal] = useState(false);
  const [showCF, setShowCF] = useState(false);
  const navigate = useNavigate();

  const handleNextClick = () => {
    navigate('/q2');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors" style={{ fontFamily: '"Pacifico", serif' }}>
            Life 마니또
          </Link>
          <Link 
            to="/" 
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-home-line"></i>
            홈으로
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Title Section */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Q1. 점액은 왜 끈끈해질까요?
          </h1>
          <p className="text-2xl text-gray-600">
            모든 것은 'CFTR'이라는 세포의 문(채널) 문제에서 시작됩니다.
          </p>
        </div>

        {/* Comparison Container */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Normal Cell */}
          <div className="bg-white rounded-3xl shadow-lg p-8 animate-slide-up">
            <h2 className="text-3xl font-bold text-green-600 mb-6 text-center">정상 세포</h2>
            
            {/* Diagram */}
            <div className="relative bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 mb-6 h-96 flex items-center justify-center">
              {/* Cell Membrane */}
              <div className="relative w-full h-full border-4 border-green-600 rounded-3xl bg-blue-100/30">
                {/* Inside Cell Label */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-700 bg-white/80 px-3 py-1 rounded-full">
                  세포 안
                </div>
                
                {/* Outside Cell Label */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-700 bg-white/80 px-3 py-1 rounded-full">
                  기도 표면 (세포 밖)
                </div>

                {/* CFTR Channel */}
                <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2">
                  <div className={`w-16 h-24 rounded-lg transition-all duration-500 ${showNormal ? 'bg-green-500' : 'bg-gray-400'} flex items-center justify-center`}>
                    <div className="text-white text-xs font-bold text-center">CFTR<br/>문</div>
                  </div>
                  {showNormal && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-semibold text-green-600 bg-white px-2 py-1 rounded animate-bounce">
                      열림
                    </div>
                  )}
                </div>

                {/* Sodium Channel */}
                <div className="absolute top-1/2 right-1/4 translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-24 bg-gray-400 rounded-lg flex items-center justify-center">
                    <div className="text-white text-xs font-bold text-center">나트륨<br/>문</div>
                  </div>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-semibold text-gray-600 bg-white px-2 py-1 rounded">
                    닫힘
                  </div>
                </div>

                {/* Ions inside cell (initial state) */}
                {!showNormal && (
                  <>
                    <div className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                      염소
                    </div>
                    <div className="absolute bottom-1/3 right-1/3 w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                      물
                    </div>
                    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                      중탄산염
                    </div>
                  </>
                )}

                {/* Ions animation (after button click) */}
                {showNormal && (
                  <>
                    {/* Chloride moving out */}
                    <div 
                      className="absolute top-1/4 left-1/4 -translate-x-1/2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0s' }}
                    >
                      Cl⁻
                    </div>
                    {/* Bicarbonate moving out */}
                    <div 
                      className="absolute top-1/3 left-1/4 -translate-x-1/2 w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.1s' }}
                    >
                      HCO₃⁻
                    </div>
                    {/* Water following */}
                    <div 
                      className="absolute top-1/2 left-1/4 -translate-x-1/2 w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.2s' }}
                    >
                      H₂O
                    </div>
                    {/* Additional water molecules */}
                    <div 
                      className="absolute top-20 left-1/4 -translate-x-1/2 w-10 h-10 bg-blue-300 rounded-full flex items-center justify-center text-xs font-bold shadow animate-fade-in-custom"
                      style={{ animationDelay: '0.3s' }}
                    >
                      H₂O
                    </div>
                    {/* pH indicator - alkaline */}
                    <div 
                      className="absolute top-16 left-1/2 -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-fade-in-custom"
                      style={{ animationDelay: '0.4s' }}
                    >
                      pH 7.4 (정상)
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Button */}
            <button
              onClick={() => setShowNormal(!showNormal)}
              className="w-full py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              {showNormal ? '초기 상태로' : '정상 세포 작동 방식 보기'}
            </button>

            {/* Explanation */}
            {showNormal && (
              <div className="mt-6 space-y-3 text-gray-700 animate-fade-in bg-green-50 p-6 rounded-xl">
                <p className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>'CFTR 문'이 정상적으로 열립니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>염소(Cl⁻)</strong>와 <strong>중탄산염(HCO₃⁻)</strong>이 세포 밖(기도 표면)으로 원활하게 나갑니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>물(H₂O)</strong>이 염소를 따라 밖으로 함께 나갑니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>중탄산염</strong>이 기도 표면의 pH를 정상(7.4)으로 유지합니다.</span>
                </p>
                <div className="mt-4 pt-4 border-t-2 border-green-200">
                  <p className="font-bold text-green-700">결과: 기도 표면이 물로 촉촉하고 정상 pH를 유지하여, 점액이 묽은 상태를 유지합니다.</p>
                </div>
              </div>
            )}
          </div>

          {/* CF Patient Cell */}
          <div className="bg-white rounded-3xl shadow-lg p-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-3xl font-bold text-red-600 mb-6 text-center">CF 환자 세포</h2>
            
            {/* Diagram */}
            <div className="relative bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 mb-6 h-96 flex items-center justify-center">
              {/* Cell Membrane */}
              <div className="relative w-full h-full border-4 border-red-600 rounded-3xl bg-red-100/30">
                {/* Inside Cell Label */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-700 bg-white/80 px-3 py-1 rounded-full">
                  세포 안
                </div>
                
                {/* Outside Cell Label */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-700 bg-white/80 px-3 py-1 rounded-full">
                  기도 표면 (세포 밖)
                </div>

                {/* Broken CFTR Channel */}
                <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-24 bg-gray-400 rounded-lg flex items-center justify-center relative">
                    <div className="text-white text-xs font-bold text-center">CFTR<br/>문</div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-1 bg-red-600 rotate-45"></div>
                      <div className="w-full h-1 bg-red-600 -rotate-45 absolute"></div>
                    </div>
                  </div>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-semibold text-red-600 bg-white px-2 py-1 rounded">
                    고장
                  </div>
                </div>

                {/* Sodium Channel */}
                <div className="absolute top-1/2 right-1/4 translate-x-1/2 -translate-y-1/2">
                  <div className={`w-16 h-24 rounded-lg transition-all duration-500 ${showCF ? 'bg-red-500' : 'bg-gray-400'} flex items-center justify-center`}>
                    <div className="text-white text-xs font-bold text-center">나트륨<br/>문</div>
                  </div>
                  {showCF && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-semibold text-red-600 bg-white px-2 py-1 rounded animate-bounce">
                      열림
                    </div>
                  )}
                </div>

                {/* Ions inside cell (initial state) */}
                {!showCF && (
                  <>
                    <div className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                      염소
                    </div>
                    <div className="absolute bottom-1/3 right-1/3 w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
                      물
                    </div>
                  </>
                )}

                {/* Ions animation (after button click) */}
                {showCF && (
                  <>
                    {/* Chloride trapped inside */}
                    <div 
                      className="absolute bottom-1/4 left-1/3 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0s' }}
                    >
                      Cl⁻
                    </div>
                    <div 
                      className="absolute bottom-1/3 left-1/2 -translate-x-1/2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.1s' }}
                    >
                      Cl⁻
                    </div>
                    {/* Bicarbonate also trapped */}
                    <div 
                      className="absolute bottom-1/2 left-1/4 w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.15s' }}
                    >
                      HCO₃⁻
                    </div>
                    {/* Sodium and water entering */}
                    <div 
                      className="absolute top-1/3 right-1/3 w-12 h-12 bg-purple-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.2s' }}
                    >
                      Na⁺
                    </div>
                    <div 
                      className="absolute bottom-1/3 right-1/3 w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center text-xs font-bold shadow-lg animate-fade-in-custom"
                      style={{ animationDelay: '0.3s' }}
                    >
                      H₂O
                    </div>
                    <div 
                      className="absolute top-1/2 right-1/4 translate-x-1/4 w-10 h-10 bg-blue-300 rounded-full flex items-center justify-center text-xs font-bold shadow animate-fade-in-custom"
                      style={{ animationDelay: '0.4s' }}
                    >
                      H₂O
                    </div>
                    {/* pH indicator - acidic */}
                    <div 
                      className="absolute top-16 left-1/2 -translate-x-1/2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-fade-in-custom"
                      style={{ animationDelay: '0.5s' }}
                    >
                      pH 6.5 (산성화)
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Button */}
            <button
              onClick={() => setShowCF(!showCF)}
              className="w-full py-4 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              {showCF ? '초기 상태로' : 'CF 환자 세포 작동 방식 보기'}
            </button>

            {/* Explanation */}
            {showCF && (
              <div className="mt-6 space-y-3 text-gray-700 animate-fade-in bg-red-50 p-6 rounded-xl">
                <p className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>'고장난 CFTR 문'이 열리지 않아 <strong>염소(Cl⁻)</strong>가 밖으로 나가지 못합니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">✗</span>
                  <span><strong>중탄산염(HCO₃⁻)</strong> 분비도 감소하여 기도 표면이 <strong>산성화(pH 6.5)</strong>됩니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>'나트륨 문'이 과도하게 활성화되어 <strong>나트륨(Na⁺)</strong>이 세포 안으로 너무 많이 들어옵니다.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">✗</span>
                  <span><strong>물(H₂O)</strong>이 나트륨을 따라 세포 안으로 빨려 들어가, 기도 표면이 '탈수' 상태가 됩니다.</span>
                </p>
                <div className="mt-4 pt-4 border-t-2 border-red-200">
                  <p className="font-bold text-red-700">결과: 물을 잃고 산성화된 점액은 비정상적으로 끈끈해지고(점도 증가) 서로 응집됩니다.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center gap-6">
          <Link 
            to="/" 
            className="px-8 py-4 bg-gray-200 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-300 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            ← 이전
          </Link>
          <button 
            onClick={handleNextClick}
            className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            다음 질문 →
          </button>
        </div>
      </main>
    </div>
  );
}
